import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import NotificationSystem, { useNotifications } from './NotificationSystem';
import './Layout.css';

interface LayoutProps {
  onLogout?: () => void;
  recurringTransactionsEnabled: boolean;
}

const Layout: React.FC<LayoutProps> = ({ onLogout, recurringTransactionsEnabled }) => {
  // Sidebar closed by default on mobile, open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024;
    }
    return false;
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { notifications, removeNotification, showSuccess } = useNotifications();

  // Handle window resize to auto-close sidebar on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = [
    { path: '/app/dashboard', label: 'Dashboard', icon: '🏠' },
    { path: '/app/transactions', label: 'Transactions', icon: '💰' },
    ...(recurringTransactionsEnabled ? [{ path: '/app/recurring', label: 'Recurring', icon: '🔄' }] : []),
    { path: '/app/cash', label: 'Cash Management', icon: '💳' },
    { path: '/app/reports', label: 'Reports', icon: '📊' },
    { path: '/app/import-export', label: 'Import/Export', icon: '📁' },
    { path: '/app/settings', label: 'Settings', icon: '⚙️' },
  ];

  const handleLogout = async () => {
    try {
      // Cancel all pending requests immediately
      apiService.cancelAllRequests();
      
      await apiService.logout();
      showSuccess('Logged Out', 'You have been successfully logged out');
      onLogout?.();
      // Immediate redirect - no delay
      navigate('/login', { replace: true });
    } catch (error) {
      // Even if logout API fails, clear tokens and redirect
      apiService.forceLogout();
      onLogout?.();
      navigate('/login', { replace: true });
    }
  };

  return (
    <div className="layout">
      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <span className="logo-icon">💰</span>
            <span className="logo-text">RokdaHisab</span>
          </div>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(false)}
            title="Close sidebar"
          >
            ✕
          </button>
        </div>
        
        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.path}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => {
                navigate(item.path);
                // Only close sidebar on mobile devices (screen width <= 1023px)
                if (window.innerWidth <= 1023) {
                  setSidebarOpen(false);
                }
              }}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </nav>
        
        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <span className="nav-icon">🚪</span>
            <span className="nav-label">Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Bar */}
        <header className="topbar">
          <button 
            className="menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title={sidebarOpen ? "Close sidebar" : "Open sidebar"}
          >
            {sidebarOpen ? "✕" : "☰"}
          </button>
          
          <div className="topbar-right">
            <div className="user-info">
              <span className="user-avatar">👤</span>
              <span className="user-name">Welcome, User</span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="page-content">
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay - Always rendered, CSS controls visibility */}
      <div 
        className={`mobile-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={() => {
          // Only close on mobile/tablet
          if (typeof window !== 'undefined' && window.innerWidth < 1024) {
            setSidebarOpen(false);
          }
        }}
      />

      {/* Notification System */}
      <NotificationSystem 
        notifications={notifications}
        onRemove={removeNotification}
      />
    </div>
  );
};

export default Layout;
